import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { estadoAtajoCeoDesdeFilas, type EstadoAtajoCeo } from "@/lib/atajosTeclado";

const CLAVE = "ia-atajo-agente-ceo";

/**
 * Atajo vigente del Agente CEO (`ia_agentes.atajo_teclado`, vía
 * `ia_agentes_disponibles()`). Cambia una sola vez en la vida del sistema, así
 * que se cachea largo. Mientras carga, o si la migración no está aplicada,
 * vale `sin_config`: el comportamiento de antes.
 */
export function useAtajoAgenteCeo(): EstadoAtajoCeo {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: [CLAVE, user?.id ?? null],
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<EstadoAtajoCeo> => {
      const { data: filas, error } = await (supabase as any).rpc("ia_agentes_disponibles");
      if (error) return "sin_config";
      return estadoAtajoCeoDesdeFilas(filas);
    },
  });
  return data ?? "sin_config";
}

/** Mueve el Agente CEO a Alt+K (⌥K en Mac) para todos (solo rol 1; definitivo). */
export function useCambiarAtajoAgenteCeo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any).rpc("ia_admin_cambiar_atajo_ceo");
      if (error) throw error;
      return data as { codigo: string; atajo_teclado: string; cambiado: boolean };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [CLAVE] }),
  });
}
